import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MinhaAreaPageShell } from '@/components/MinhaAreaPageShell';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  ArrowLeft,
  CheckCircle2,
  CreditCard,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { useAuthState } from '@/hooks/useAuthState';
import SubscriptionPlanBadge from '@/components/SubscriptionPlanBadge';
import {
  canManagePropertiesOnPlatform,
  getPlanMeta,
  hasFullPlatformAccess,
  normalizePlanSlug,
} from '@/lib/subscriptionPlanMeta';
import { isOwnerUser, resolveUserType } from '@/lib/livePlanAccess';
import { cn } from '@/lib/utils';

const MinhaAssinatura = () => {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    subscriptionPlan,
    userType,
    user,
    userName,
    loading: authLoading,
    refreshUser,
  } = useAuthState();
  const effectiveUserType = resolveUserType(userType, user);
  const planSlug = normalizePlanSlug(
    subscriptionPlan,
    user?.plan_id as number | undefined,
  );
  const planMeta = getPlanMeta(subscriptionPlan, user?.plan_id);
  const isOwner = isOwnerUser(effectiveUserType);
  const fullAccess = hasFullPlatformAccess(planSlug);
  const canManageProps = canManagePropertiesOnPlatform(planSlug);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      void refreshUser();
    }
  }, [authLoading, isAuthenticated, refreshUser]);

  if (authLoading) {
    return (
      <MinhaAreaPageShell contentClassName="container mx-auto px-4 py-16 flex justify-center items-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden />
          <p className="text-muted-foreground">Carregando seu plano...</p>
        </div>
      </MinhaAreaPageShell>
    );
  }

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  if (!isOwner) {
    return (
      <MinhaAreaPageShell contentClassName="container mx-auto px-4 py-8 max-w-lg">
        <Card>
          <CardHeader>
            <CardTitle>Minha assinatura</CardTitle>
            <CardDescription>
              Planos pagos são exclusivos para proprietários. Sua conta é de
              hóspede (guest).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => navigate('/minha-area')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar para Minha Área
            </Button>
          </CardContent>
        </Card>
      </MinhaAreaPageShell>
    );
  }

  return (
    <MinhaAreaPageShell contentClassName="container mx-auto px-4 py-8 max-w-2xl">
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 -ml-2 mb-4"
          onClick={() => navigate('/minha-area')}
        >
          <ArrowLeft className="h-4 w-4" />
          Minha Área
        </Button>
        <h1 className="text-2xl font-semibold text-foreground">Minha assinatura</h1>
        <p className="text-muted-foreground mt-1">
          Plano atual de {userName || 'sua conta'}
        </p>
      </div>

      <Card className="shadow-lg overflow-hidden">
        <div
          className={cn(
            'h-1.5 w-full',
            planSlug === 'live' && 'bg-gradient-to-r from-orange-500 to-amber-500',
            planSlug === 'porto2life' && 'bg-gradient-to-r from-sky-600 to-blue-700',
            planSlug === 'freemium' && 'bg-slate-300 dark:bg-slate-600',
          )}
        />
        <CardHeader className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-xl">
              <CreditCard className="h-5 w-5" aria-hidden />
              Plano contratado
            </CardTitle>
            <SubscriptionPlanBadge
              plan={subscriptionPlan}
              planId={user?.plan_id}
              size="lg"
            />
          </div>
          <CardDescription className="text-base leading-relaxed">
            {planMeta.description}
          </CardDescription>
          {fullAccess && (
            <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              Você tem acesso a todas as funcionalidades da Live Stay.
            </p>
          )}
          {planSlug === 'porto2life' && (
            <p className="text-sm text-muted-foreground">
              Para liberar imóveis e reservas em{' '}
              <strong>todos os resorts</strong>, faça upgrade para o plano Live.
            </p>
          )}
        </CardHeader>

        <CardContent className="space-y-6">
          <div>
            <h2 className="text-sm font-semibold text-foreground mb-3">
              O que seu plano inclui
            </h2>
            <ul className="space-y-2">
              {planMeta.highlights.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2 text-sm text-muted-foreground"
                >
                  <CheckCircle2
                    className={cn(
                      'h-4 w-4 shrink-0 mt-0.5',
                      fullAccess
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-primary',
                    )}
                  />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {!canManageProps && (
            <div className="rounded-lg border border-dashed border-orange-200 dark:border-orange-900/50 bg-orange-50/80 dark:bg-orange-950/20 p-4 text-sm">
              <p className="font-medium text-foreground mb-1">Quer ser proprietário na rede?</p>
              <p className="text-muted-foreground mb-3">
                O plano Live desbloqueia cadastro de imóveis, reservas e gestão completa.
              </p>
              <Button
                className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
                onClick={() => navigate('/minha-area/assinar-live')}
              >
                <Sparkles className="h-4 w-4" />
                Conhecer plano Live
              </Button>
            </div>
          )}

          {planSlug === 'porto2life' && (
            <Button
              variant="outline"
              className="w-full sm:w-auto gap-2 border-orange-200 text-orange-700 hover:bg-orange-50 dark:border-orange-900 dark:text-orange-300"
              onClick={() => navigate('/minha-area/assinar-live')}
            >
              <Sparkles className="h-4 w-4" />
              Fazer upgrade para Live
            </Button>
          )}

          {planSlug === 'freemium' && isOwner && (
            <Button
              className="w-full sm:w-auto gap-2 bg-orange-500 hover:bg-orange-600 text-white"
              onClick={() => navigate('/minha-area/assinar-live')}
            >
              <Sparkles className="h-4 w-4" />
              Assinar plano Live
            </Button>
          )}
        </CardContent>
      </Card>
    </MinhaAreaPageShell>
  );
};

export default MinhaAssinatura;
